import { shallowEqual, useDispatch } from 'react-redux';
import T from 'i18n';
import {
  CASE_SET_AI_SEARCH_MATCHES,
  CASE_UPDATE_AI_SEARCH_MATCHES,
  CASE_CLEAR_AI_SEARCH_MATCHES,
  CASE_SET_AI_SEARCH_ANSWER_CHAT,
  CASE_SET_AI_SEARCH_SUGGESTED_QUESTIONS,
  CASE_SET_AI_CLICKED_SOURCE,
  CASE_CLEAR_CLICKED_SOURCE_STATE,
  CASE_CLEAR_AI_SEARCH_STATE,
  CASE_SET_DIRECT_ANSWERS_FILTERS,
} from './constants';
import { useSelector } from 'react-redux';
import { useCallback } from 'react';

export function setAISearchMatches(payload) {
  return {
    type: CASE_SET_AI_SEARCH_MATCHES,
    payload,
  };
}

export function updateAISearchMatches(id, payload) {
  return {
    type: CASE_UPDATE_AI_SEARCH_MATCHES,
    id,
    payload,
  };
}

export function clearAISearchMatches() {
  return {
    type: CASE_CLEAR_AI_SEARCH_MATCHES,
  };
}

export function setAISearchAnswerChat(payload) {
  return {
    type: CASE_SET_AI_SEARCH_ANSWER_CHAT,
    payload,
  };
}

export function setAISearchSuggestedQuestions(payload) {
  return {
    type: CASE_SET_AI_SEARCH_SUGGESTED_QUESTIONS,
    payload,
  };
}

export function setDirectAnswersFilters(payload) {
  return {
    type: CASE_SET_DIRECT_ANSWERS_FILTERS,
    payload,
  };
}

export function setAIClickedSource(payload) {
  return {
    type: CASE_SET_AI_CLICKED_SOURCE,
    payload,
  };
}

export function clearAISearchState() {
  return {
    type: CASE_CLEAR_AI_SEARCH_STATE,
  };
}

export function clearClickedSourceState() {
  return {
    type: CASE_CLEAR_CLICKED_SOURCE_STATE,
  };
}

export function useSetAISearchState() {
  const dispatch = useDispatch();

  // In test environments a heavily mocked useSelector implementation may return null/undefined.
  // Provide robust fallback handling so component tests that don't mount the full case reducer don't crash.
  const aiSearchState =
    useSelector((state) => {
      const safeCase = state && state.case ? state.case : {};
      return {
        searchMatchesInput: safeCase.searchMatchesInput,
        directAnswersFilters: safeCase.directAnswersFilters,
        searchAnsChat: safeCase.searchAnsChat,
        searchSuggestedQuestions: safeCase.searchSuggestedQuestions,
        clickedSource: safeCase.clickedSource,
      };
    }, shallowEqual) || {};

  // Apply default fallbacks so downstream code can rely on types.
  const searchMatchesInput = aiSearchState.searchMatchesInput || [];
  const directAnswersFilters = aiSearchState.directAnswersFilters || null;
  const searchAnsChat = aiSearchState.searchAnsChat || [];
  const searchSuggestedQuestions = aiSearchState.searchSuggestedQuestions || null;
  const clickedSource = aiSearchState.clickedSource || null;

  const boundActionSetAISearchMatches = useCallback(
    (...args) => {
      return dispatch(setAISearchMatches(...args));
    },
    [dispatch],
  );

  const boundActionUpdateAISearchMatches = useCallback(
    (...args) => {
      return dispatch(updateAISearchMatches(...args));
    },
    [dispatch],
  );

  const boundActionClearAISearchMatches = useCallback(
    (...args) => {
      return dispatch(clearAISearchMatches(...args));
    },
    [dispatch],
  );

  const boundActionSetAISearchAnswerChat = useCallback(
    (...args) => {
      return dispatch(setAISearchAnswerChat(...args));
    },
    [dispatch],
  );

  const boundActionSetAISearchSuggestedQuestions = useCallback(
    (...args) => {
      return dispatch(setAISearchSuggestedQuestions(...args));
    },
    [dispatch],
  );

  const boundActionSetDirectAnswersFilters = useCallback(
    (...args) => {
      return dispatch(setDirectAnswersFilters(...args));
    },
    [dispatch],
  );

  const boundActionSetAIClickedSource = useCallback(
    (...args) => {
      return dispatch(setAIClickedSource(...args));
    },
    [dispatch],
  );

  const boundActionClearAISearchState = useCallback(
    (...args) => {
      return dispatch(clearAISearchState(...args));
    },
    [dispatch],
  );

  return {
    setAISearchMatches: boundActionSetAISearchMatches,
    updateAISearchMatches: boundActionUpdateAISearchMatches,
    clearAISearchMatches: boundActionClearAISearchMatches,
    setAISearchAnswerChat: boundActionSetAISearchAnswerChat,
    setAISearchSuggestedQuestions: boundActionSetAISearchSuggestedQuestions,
    setDirectAnswersFilters: boundActionSetDirectAnswersFilters,
    setAIClickedSource: boundActionSetAIClickedSource,
    clearAISearchState: boundActionClearAISearchState,
    searchMatchesInput: searchMatchesInput,
    directAnswersFilters: directAnswersFilters,
    searchAnsChat: searchAnsChat,
    searchSuggestedQuestions: searchSuggestedQuestions,
    clickedSource: clickedSource,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case CASE_SET_AI_SEARCH_MATCHES:
      return { ...state, searchMatchesInput: [...state.searchMatchesInput, action.payload] };
    case CASE_UPDATE_AI_SEARCH_MATCHES:
      return {
        ...state,
        searchMatchesInput: state.searchMatchesInput.map((itm) =>
          itm.id === action.id ? { ...itm, ...action.payload } : itm,
        ),
      };
    case CASE_CLEAR_AI_SEARCH_MATCHES:
      return { ...state, searchMatchesInput: [] };
    case CASE_SET_AI_SEARCH_ANSWER_CHAT:
      return {
        ...state,
        searchAnsChat: [
          ...state.searchAnsChat.filter((item) => item.text !== T.translate('case.searchingText')),
          ...action.payload,
        ],
      };
    case CASE_SET_AI_SEARCH_SUGGESTED_QUESTIONS:
      return { ...state, searchSuggestedQuestions: action.payload };
    case CASE_SET_AI_CLICKED_SOURCE:
      return { ...state, clickedSource: action.payload };
    case CASE_CLEAR_AI_SEARCH_STATE:
      return {
        ...state,
        searchMatchesInput: [],
        fileSearchHistory: [],
        searchAnsChat: [],
        citations: [],
        relevantFiles: [],
        files: [],
        location: [],
        responseType: '',
        searchSuggestedQuestions: null,
        directAnswersFilters: null,
      };
    case CASE_SET_DIRECT_ANSWERS_FILTERS:
      return { ...state, directAnswersFilters: action.payload };
    case CASE_CLEAR_CLICKED_SOURCE_STATE:
      return {
        ...state,
        clickedSource: null,
      };
    default:
      return state;
  }
}
